import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * Formation & Initiatives
 *
 * Editorial split-screen: a tall left column with the section eyebrow, headline,
 * subheading, manifesto callout and primary CTA — held against a sticky vertical
 * rule. The right column is a chronological, divider-led list of initiatives;
 * each entry uses an oversized italic numeral, a serif title, a quiet body
 * paragraph and a subtle hover wash that breathes the brand's blue ambient
 * lighting across the row.
 *
 * The layout deliberately rejects card-grid repetition in favour of long-form,
 * book-like pacing.
 */

const INITIATIVES = [
  {
    n: "01",
    title: "Youth Mentorship Initiative",
    body:
      "A tarbiyah-centered mentorship experience helping young Muslims develop identity, discipline, emotional resilience, leadership, and purpose.",
    cta: "Learn More",
  },
  {
    n: "02",
    title: "Youth Coaching & Guidance",
    body:
      "One-to-one mentorship for young men navigating faith, motivation, emotional growth, leadership, and personal development.",
    cta: "Learn More",
  },
  {
    n: "03",
    title: "Educator Development",
    body:
      "Professional development workshops helping educators move beyond information delivery toward transformative teaching and Murabbi-centered education.",
    cta: "Learn More",
  },
  {
    n: "04",
    title: "Webinars & Seminars",
    body:
      "Conversations and training sessions on mentorship, emotional intelligence, education, tarbiyah, leadership, and youth development.",
    cta: "Learn More",
  },
  {
    n: "05",
    title: "Future Institutional Vision",
    body:
      "Long-term projects focused on educational leadership, curriculum philosophy, and building tarbiyah-centered institutions for the next generation.",
    cta: "Explore Vision",
  },
];

const ConsultingSection = ({ section }) => {
  return (
    <section
      data-testid={`featured-section-${section.id}`}
      className="relative overflow-hidden bg-ink"
    >
      {/* Ambient lighting — slow vertical wash + corner glows */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 45% 55% at 78% 18%, rgba(74,143,229,0.12), transparent 65%), radial-gradient(ellipse 50% 60% at 8% 88%, rgba(74,143,229,0.07), transparent 70%), radial-gradient(ellipse 30% 25% at 50% 50%, rgba(201,169,97,0.035), transparent 75%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 inset-x-0 h-40"
        style={{ background: "linear-gradient(to bottom, rgba(6,19,37,0.7), transparent)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 inset-x-0 h-40"
        style={{ background: "linear-gradient(to top, rgba(6,19,37,0.55), transparent)" }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-18 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-24">
          {/* ── Left: editorial statement column ──────────────────────── */}
          <div className="lg:col-span-5 relative">
            <div className="lg:sticky lg:top-32">
              {/* Eyebrow */}
              <div className="flex items-center gap-4">
                <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
                  {section.eyebrow}
                </span>
                <span className="h-px w-12 bg-azure-soft/50" />
              </div>

              {/* Headline — two-line treatment */}
              <h2 className="mt-10 font-serif text-white text-[32px] sm:text-[42px] lg:text-[50px] leading-[1.02] tracking-[-0.022em]">
                {section.headlineA}
              </h2>
              <h2 className="font-serif italic text-azure-soft text-[32px] sm:text-[42px] lg:text-[50px] leading-[1.02] tracking-[-0.022em]">
                {section.headlineB}
              </h2>

              {/* Hair-line rule */}
              <div className="mt-10 h-px w-20 bg-gold/80" />

              {/* Subheading */}
              <p className="mt-9 text-white/70 text-[16px] leading-[1.95] max-w-md">
                {section.intro}
              </p>

              {/* Manifesto callout */}
              <figure className="mt-12 relative">
                <span
                  aria-hidden="true"
                  className="absolute -left-3 -top-6 font-serif text-azure-soft/45 text-[90px] leading-none select-none pointer-events-none"
                >
                  “
                </span>
                <blockquote className="relative pl-7 border-l border-azure-soft/30">
                  <p className="font-serif text-white/90 text-[16px] lg:text-[18px] leading-[1.45] tracking-[-0.005em]">
                    We do not need more programs.
                  </p>
                  <p className="font-serif italic text-azure-soft text-[16px] lg:text-[18px] leading-[1.45] tracking-[-0.005em]">
                    We need formation.
                  </p>
                </blockquote>
                <figcaption className="mt-5 pl-7 text-[10.5px] tracking-[0.34em] uppercase text-white/45">
                  — The Murabbi Method
                </figcaption>
              </figure>

              {/* Primary CTA */}
              <Link
                to={section.viewAllHref}
                data-testid={`featured-view-all-${section.id}`}
                className="group inline-flex items-center gap-3 mt-14 text-[11px] tracking-[0.34em] uppercase text-azure-soft hover:text-azure border-b border-azure-soft/60 hover:border-azure pb-2.5 transition-colors"
              >
                Begin a conversation
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1.5 group-hover:-translate-y-1.5" />
              </Link>
            </div>
          </div>

          {/* ── Right: vertically stacked initiatives ─────────────────── */}
          <div className="lg:col-span-7 lg:pl-2 relative">
            {/* Subtle vertical guideline */}
            <div
              aria-hidden="true"
              className="hidden lg:block absolute -left-12 top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-azure-soft/15 to-transparent"
            />

            <ol className="space-y-0">
              {INITIATIVES.map((item, i) => (
                <li
                  key={item.n}
                  data-testid={`featured-card-${section.id}-${i}`}
                  className="group relative"
                >
                  {/* Top divider */}
                  <div className="h-px bg-white/10 group-hover:bg-azure-soft/40 transition-colors duration-700" />

                  {/* Hover wash */}
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 inset-y-3 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(74,143,229,0.07) 0%, rgba(74,143,229,0.02) 55%, transparent 100%)",
                    }}
                  />

                  <Link
                    to={section.viewAllHref}
                    className="relative block py-10 lg:py-14"
                  >
                    <div className="grid grid-cols-12 gap-6 lg:gap-10 items-start">
                      {/* Numeral */}
                      <div className="col-span-2 sm:col-span-1 lg:col-span-2">
                        <span className="block font-serif italic text-azure-soft text-[36px] lg:text-[52px] leading-none tracking-[-0.02em]">
                          {item.n}
                        </span>
                      </div>

                      {/* Body */}
                      <div className="col-span-10 sm:col-span-11 lg:col-span-10">
                        <h3 className="font-serif text-white text-[19px] lg:text-[24px] leading-[1.18] tracking-[-0.015em] transition-colors duration-500 group-hover:text-azure-soft">
                          {item.title}
                        </h3>
                        <p className="mt-5 text-white/65 text-[15px] leading-[1.9] max-w-[60ch]">
                          {item.body}
                        </p>
                        <div className="mt-7 inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-white/55 group-hover:text-azure-soft transition-colors duration-500">
                          {item.cta}
                          <ArrowUpRight
                            className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1.5 group-hover:-translate-y-1.5"
                            strokeWidth={1.6}
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
              {/* Final closing divider for symmetry */}
              <li aria-hidden="true">
                <div className="h-px bg-white/10" />
              </li>
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultingSection;
